/* eslint-disable complexity */
import { useEffect, useRef, useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { IconButton } from "@atlaskit/button-next/new";
import { NewCoreIconProps } from "@atlaskit/icon";
import CrossIcon from "@atlaskit/icon/core/cross";
import LinkIcon from "@atlaskit/icon/core/link";
import {
    BitbucketIcon,
    CompassIcon,
    ConfluenceIcon,
    GuardIcon,
    JiraProductDiscoveryIcon,
    JiraServiceManagementIcon,
    JiraAlignIcon,
    JiraIcon,
    LoomIcon,
    OpsgenieIcon,
    RovoIcon,
    StatuspageIcon,
    TrelloIcon,
    AtlassianIcon
} from "@atlaskit/logo";
import { token } from "@atlaskit/tokens";

import { Button } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";

import { SearchHelper } from "../search-helper";

import { Source } from "./ai-summary";
import { useAISummaryContext } from "./ai-summary-context";

// Placeholder skeletons for sources while loading
const sourcePlaceholderPulseStyle = css({
    "position": "relative",
    "overflow": "hidden",
    "&::after": {
        content: "''",
        position: "absolute",
        inset: 0,
        borderRadius: "inherit",
        background: `linear-gradient(
            90deg,
            transparent 0%,
            ${token("color.background.neutral.subtle.hovered")} 50%,
            transparent 100%
        )`,
        transform: "translateX(-100%)",
        animation:
            "sourcePlaceholderShimmer 1200ms cubic-bezier(0.4, 0, 0.2, 1) infinite"
    },
    "@keyframes sourcePlaceholderShimmer": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "0%": {
            transform: "translateX(-100%)"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "100%": {
            transform: "translateX(100%)"
        }
    },
    "@media (prefers-reduced-motion: reduce)": {
        "&::after": {
            animation: "none"
        }
    }
});

const sourcesIconPlaceholderStyle = css({
    width: token("space.250"),
    height: token("space.250"),
    borderRadius: token("space.050"),
    backgroundColor: wacToken("color.extended.blue100")
});

const sourceDescriptionPlaceholderStyle = css({
    width: "100%",
    height: token("space.250"),
    backgroundColor: wacToken("color.extended.blue100"),
    borderRadius: token("border.radius.100")
});

const sourceTitlePlaceholderStyle = css({
    width: "60%",
    height: token("space.250"),
    backgroundColor: wacToken("color.extended.blue100"),
    borderRadius: token("border.radius.100")
});

const AISourcePlaceholder = () => {
    return (
        <div css={sourceStyle}>
            <div css={sourceHeaderStyle}>
                <div
                    css={[
                        sourcesIconPlaceholderStyle,
                        sourcePlaceholderPulseStyle
                    ]}
                />
                <div
                    css={[
                        sourceTitlePlaceholderStyle,
                        sourcePlaceholderPulseStyle
                    ]}
                />
            </div>
            <div
                css={[
                    sourceDescriptionPlaceholderStyle,
                    sourcePlaceholderPulseStyle
                ]}
            />
            <div
                css={[
                    sourceDescriptionPlaceholderStyle,
                    sourcePlaceholderPulseStyle
                ]}
            />
        </div>
    );
};

const createProductMatcher = (
    logo: typeof BitbucketIcon,
    productName: string
) => ({
    logo,
    patterns: [`/${productName}`, `/software/${productName}`]
});

const productLogoMatchers: Array<{
    logo: typeof BitbucketIcon;
    patterns: string[];
}> = [
    createProductMatcher(BitbucketIcon, "bitbucket"),
    createProductMatcher(CompassIcon, "compass"),
    createProductMatcher(ConfluenceIcon, "confluence"),
    createProductMatcher(GuardIcon, "guard"),
    createProductMatcher(JiraProductDiscoveryIcon, "jira/product-discovery"),
    createProductMatcher(JiraServiceManagementIcon, "jira/service-management"),
    createProductMatcher(JiraAlignIcon, "jira/align"),
    createProductMatcher(JiraIcon, "jira"),
    createProductMatcher(LoomIcon, "loom"),
    createProductMatcher(OpsgenieIcon, "opsgenie"),
    createProductMatcher(RovoIcon, "rovo"),
    createProductMatcher(RovoIcon, "rovo-dev"), // Using RovoIcon until RovoDevIcon is available. Packages may need updating to work.
    createProductMatcher(StatuspageIcon, "statuspage"),
    createProductMatcher(TrelloIcon, "trello")
];

const getSourceProductLogo = (sourceUrl: string) => {
    let normalizedUrl = sourceUrl.toLowerCase();

    try {
        const parsedUrl = new URL(sourceUrl);
        normalizedUrl =
            `${parsedUrl.hostname}${parsedUrl.pathname}`.toLowerCase();
    } catch {
        normalizedUrl = sourceUrl.toLowerCase();
    }

    const matchedLogo = productLogoMatchers.find(({ patterns }) =>
        patterns.some((pattern) => normalizedUrl.includes(pattern))
    );

    return matchedLogo?.logo || AtlassianIcon;
};

const sourcesContainerStyle = css({
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    position: "relative",
    height: "249px",
    maxWidth: "467px",
    borderRadius: token("border.radius.200")
});

const sourcesContainerExpandedStyle = css({
    height: "auto"
});

const sourcesStyle = css({
    display: "flex",
    flexDirection: "column",
    paddingInlineStart: token("space.200"),
    paddingInlineEnd: token("space.200"),
    borderRadius: token("border.radius.200"),
    backgroundColor: wacToken("color.extended.blue100")
});

const sourcesExpandedScrollableStyle = css({
    flex: 1,
    minHeight: 0,
    overflowY: "auto"
});

const sourcesFilteredStyle = css({
    border: `1px solid ${wacToken("color.extended.blue200")}`,
    borderRadius: token("border.radius.200"),
    padding: token("space.200")
});

const sourcesWithButtonStyle = css({
    borderRadius: `${token("border.radius.200")} ${token(
        "border.radius.200"
    )} 0 0`,
    paddingBlockEnd: 0
});

// This is shared between the placeholder and the actual sources to prevent layout shift when loading state changes
const sourceStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "center",
    textAlign: "left",
    gap: token("space.100"),
    padding: "0",
    paddingBlockStart: token("space.200"),
    paddingBlockEnd: token("space.200"),
    border: "none",
    borderBlockEnd: `1px solid ${wacToken("color.extended.blue200")}`,
    overflow: "hidden",
    textOverflow: "ellipsis",
    background: "none",
    cursor: "pointer",
    font: "inherit"
});

const sourceLastWithoutButtonStyle = css({
    borderBlockEnd: "none"
});

const sourceHeaderStyle = css({
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    width: "100%",
    gap: token("space.100")
});

const sourceTitleStyle = css({
    minWidth: 0,
    flex: "1 1 auto",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
});

const sourceDescriptionStyle = css({
    display: "-webkit-box",
    overflow: "hidden",
    margin: 0,
    color: wacToken("color.neutral.light800"),
    fontSize: token("font.body.small"),
    textOverflow: "ellipsis",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical"
});

const sourcesHeaderStyle = css({
    display: "flex",
    alignItems: "center",
    gap: token("space.050"),
    marginBlockEnd: token("space.150")
});

const sourcesHeaderTitleStyle = css({
    flex: 1
});

const sourcesIconWrapperStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: token("space.300"),
    height: token("space.300"),
    borderRadius: token("space.050"),
    backgroundColor: wacToken("color.extended.blue100")
});

const blurOverlayStyle = css({
    background: `linear-gradient(180deg, transparent 0%, ${token(
        "elevation.surface.overlay",
        wacToken("color.extended.blue100")
    )} 50%)`,
    width: "100%",
    display: "inline-flex",
    justifyContent: "center",
    alignItems: "center",
    position: "absolute",
    insetBlockEnd: 0,
    insetInlineStart: 0,
    paddingBlockStart: token("space.800"),
    color: token("color.text")
});

const showMoreButtonBlurOverlayStyle = css({
    background: `linear-gradient(180deg, transparent 0%, ${wacToken(
        "color.extended.blue100"
    )} 60%)`,
    width: "100%",
    height: token("space.600"),
    position: "absolute",
    insetInlineStart: 0,
    insetBlockEnd: "52px",
    pointerEvents: "none",
    zIndex: 1
});

const buttonStyle = css({
    "display": "flex",
    "flexShrink": 0,
    "alignItems": "center",
    "justifyContent": "center",
    "width": "100%",
    "height": "52px",
    "minHeight": "52px",
    "maxWidth": "none !important",
    "backgroundColor": token("color.background.accent.blue.subtlest"),
    "borderRadius": `0 0 ${token("border.radius.200")} ${token(
        "border.radius.200"
    )}`,
    "color": token("color.icon.brand"),

    "&&:hover": {
        backgroundColor: `${token(
            "color.background.accent.blue.subtlest.hovered"
        )}`
    }
});

// Converts a URL to an Algolia objectId following the pattern: https://www.atlassian.com/software/confluence -> www.atlassian.com-software-confluence
// Uses plain string operations instead of regex to avoid ReDoS risk from backtracking.
const urlToObjectId = (url: string): string => {
    // Strip protocol prefix (http:// or https://) without regex
    const protocolEnd = url.indexOf("://");
    let normalized = protocolEnd !== -1 ? url.slice(protocolEnd + 3) : url;
    // Strip trailing slashes
    let end = normalized.length;
    while (end > 0 && normalized[end - 1] === "/") {
        end -= 1;
    }
    normalized = normalized.slice(0, end);
    // Replace path separators with hyphens
    return normalized.split("/").join("-");
};

const ALGOLIA_SEARCH_INDEX_NAME_V3_2 = "www.atlassian.com.v3-2";
const ALGOLIA_DEV_SEARCH_INDEX_V3 = "www.atlassian.com.v3_dev";

const CrossIconComponent = (iconProps: NewCoreIconProps) => {
    return <CrossIcon {...iconProps} size="small" />;
};

interface AISummarySourcesProps {
    isExpanded: boolean;
    isTruncated: boolean;
    maxExpandedHeight: number;
    filteredSourceIndexes: number[] | null;
    onClearSourceFilter: () => void;
}

interface SourceDescription {
    [key: number]: string;
}

// eslint-disable-next-line complexity
export const AISummarySources = ({
    isExpanded,
    isTruncated,
    maxExpandedHeight,
    filteredSourceIndexes,
    onClearSourceFilter
}: AISummarySourcesProps) => {
    const [isSourcesExpanded, setIsSourcesExpanded] = useState(false);
    const [filteredListMaxHeight, setFilteredListMaxHeight] = useState(264);
    const [sourceDescriptions, setSourceDescriptions] =
        useState<SourceDescription>({});
    const [isLoadingDescriptions, setIsLoadingDescriptions] = useState(false);
    const sourcesListRef = useRef<HTMLDivElement>(null);
    const searchHelperRef = useRef<SearchHelper>();

    const { sources } = useAISummaryContext();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const intl = useIntl();

    const isFilteredMode = Boolean(filteredSourceIndexes?.length);
    const filteredSources = (sources ?? []).filter((_, index) =>
        (filteredSourceIndexes ?? []).includes(index)
    );

    const visibleSources = isSourcesExpanded
        ? sources ?? []
        : (sources ?? []).slice(0, 3);
    const displaySources = isFilteredMode ? filteredSources : visibleSources;
    const shouldShowMoreButton = !isFilteredMode && (sources?.length ?? 0) > 3;

    const isSourcesLoading = !sources;

    useEffect(() => {
        if (!isFilteredMode || !sourcesListRef.current) {
            return;
        }

        const firstRow = sourcesListRef.current
            .firstElementChild as HTMLElement | null;

        if (!firstRow) {
            return;
        }

        setFilteredListMaxHeight(firstRow.offsetHeight * 3);
    }, [displaySources.length, isFilteredMode]);

    // Fetch descriptions from Algolia using SearchHelper
    useEffect(() => {
        if (!sources || sources.length === 0) {
            return;
        }

        const fetchDescriptions = async () => {
            try {
                setIsLoadingDescriptions(true);

                // Initialize SearchHelper if not already done
                if (!searchHelperRef.current) {
                    const searchIndex =
                        process.env["NODE_ENV"] === "production"
                            ? ALGOLIA_SEARCH_INDEX_NAME_V3_2
                            : ALGOLIA_DEV_SEARCH_INDEX_V3;
                    searchHelperRef.current = new SearchHelper(searchIndex, [
                        "category",
                        "locale"
                    ]);
                }

                // Convert URLs to objectIds
                const objectIds = sources.map((source) =>
                    urlToObjectId(source.url)
                );

                // Fetch objects from Algolia
                const response = await searchHelperRef.current.getObjects(
                    objectIds
                );

                // Map descriptions to source indexes
                if (response && response.results) {
                    const descriptionsMap: SourceDescription = {};
                    response.results.forEach(
                        (result: unknown, index: number) => {
                            const typedResult = result as {
                                description?: string;
                            };

                            if (typedResult.description) {
                                descriptionsMap[index] =
                                    typedResult.description;
                            }
                        }
                    );
                    setSourceDescriptions(descriptionsMap);
                }
            } catch (error) {
                console.error(
                    "Failed to fetch source descriptions from Algolia:",
                    error
                );
            } finally {
                setIsLoadingDescriptions(false);
            }
        };

        fetchDescriptions();
    }, [sources]);

    const handleSourceLinkClick = (source: Source, index: number) => {
        createAnalyticsEvent({
            eventName: "searchAiSummarySourceLinkClicked",
            eventType: "ui",
            action: "clicked",
            actionSubject: "link",
            actionSubjectId: "searchAiSummarySourceLink",
            source: "searchAiSummary",
            attributes: {
                product: "search",
                sourceUrl: source.url,
                sourceTitle: source.title,
                sourceIndex: index
            }
        }).fire();

        window.open(source.url, "_blank", "noopener,noreferrer");
    };

    const handleToggleSourcesExpansion = () => {
        setIsSourcesExpanded(!isSourcesExpanded);

        createAnalyticsEvent({
            eventName: isSourcesExpanded
                ? "searchAiSummarySourcesShowLessClicked"
                : "searchAiSummarySourcesShowMoreClicked",
            eventType: "ui",
            action: "clicked",
            actionSubject: "button",
            actionSubjectId: isSourcesExpanded
                ? "searchAiSummarySourcesShowLess"
                : "searchAiSummarySourcesShowMore",
            source: "searchAiSummary",
            attributes: {
                product: "search",
                labelEnglish: isSourcesExpanded ? "Show less" : "Show more",
                sourceCount: sources?.length ?? 0
            }
        }).fire();
    };

    const sourcesText = intl.formatMessage({
        id: "ai.summary.sources.header",
        defaultMessage: sources && sources.length > 1 ? "Sources" : "Source"
    });

    const relevantSourcesText = intl.formatMessage({
        id: "ai.summary.sources.relevant.header",
        defaultMessage: "Relevant sources"
    });

    const clearRelevantSourcesFilterText = intl.formatMessage({
        id: "ai.summary.sources.relevant.clear",
        defaultMessage: "Clear relevant sources filter"
    });

    const showMoreButtonText = intl.formatMessage({
        id: "ai.summary.sources.showMore",
        defaultMessage: isSourcesExpanded ? "Show less" : "Show more"
    });

    return (
        <div
            css={[
                sourcesContainerStyle,
                isExpanded && sourcesContainerExpandedStyle,
                isFilteredMode && sourcesFilteredStyle
            ]}
            style={
                !isFilteredMode && isSourcesExpanded
                    ? { maxHeight: `${maxExpandedHeight}px` }
                    : undefined
            }
        >
            <div css={sourcesHeaderStyle}>
                <div css={sourcesIconWrapperStyle}>
                    <LinkIcon
                        size="medium"
                        label={
                            isFilteredMode ? relevantSourcesText : sourcesText
                        }
                        color={token("color.icon.brand")}
                    />
                </div>
                <div css={sourcesHeaderTitleStyle}>
                    {isFilteredMode ? relevantSourcesText : sourcesText}
                </div>
                {isFilteredMode && (
                    <IconButton
                        appearance="subtle"
                        icon={(iconProps) => CrossIconComponent(iconProps)}
                        label={clearRelevantSourcesFilterText}
                        onClick={onClearSourceFilter}
                    />
                )}
            </div>
            <div
                ref={sourcesListRef}
                css={[
                    sourcesStyle,
                    shouldShowMoreButton && sourcesWithButtonStyle,
                    (isSourcesExpanded || isFilteredMode) &&
                        sourcesExpandedScrollableStyle
                ]}
                style={
                    isFilteredMode
                        ? {
                              maxHeight: `${Math.min(
                                  filteredListMaxHeight,
                                  maxExpandedHeight
                              )}px`
                          }
                        : undefined
                }
            >
                {isSourcesLoading || isLoadingDescriptions
                    ? Array.from({ length: 2 }).map((_, index) => (
                          <AISourcePlaceholder key={index} />
                      ))
                    : displaySources.map((source, i) => {
                          // Get the actual index in the original sources array
                          const actualSourceIndex = (sources ?? []).findIndex(
                              (s) => s.url === source.url
                          );
                          const description =
                              sourceDescriptions[actualSourceIndex] || "";

                          return (
                              <button
                                  key={source.url}
                                  tabIndex={0}
                                  aria-label={source.title || source.url}
                                  css={[
                                      sourceStyle,
                                      !shouldShowMoreButton &&
                                          i === displaySources.length - 1 &&
                                          sourceLastWithoutButtonStyle
                                  ]}
                                  onClick={() =>
                                      handleSourceLinkClick(source, i)
                                  }
                                  onKeyDown={(event) => {
                                      if (
                                          event.key === "Enter" ||
                                          event.key === " "
                                      ) {
                                          event.preventDefault();
                                          handleSourceLinkClick(source, i);
                                      }
                                  }}
                              >
                                  <div css={sourceHeaderStyle}>
                                      {(() => {
                                          const SourceLogo =
                                              getSourceProductLogo(source.url);

                                          return (
                                              <SourceLogo
                                                  size="small"
                                                  appearance="brand"
                                              />
                                          );
                                      })()}
                                      <div css={sourceTitleStyle}>
                                          {source.title}
                                      </div>
                                  </div>
                                  <p css={sourceDescriptionStyle}>
                                      {description}
                                  </p>
                              </button>
                          );
                      })}
            </div>
            {!isFilteredMode && !isExpanded && isTruncated && (
                <div css={blurOverlayStyle} />
            )}
            {!isFilteredMode &&
                isExpanded &&
                shouldShowMoreButton &&
                !isSourcesLoading &&
                !isSourcesExpanded && (
                    <div css={showMoreButtonBlurOverlayStyle} />
                )}
            {!isFilteredMode && shouldShowMoreButton && !isSourcesLoading && (
                <Button
                    css={buttonStyle}
                    onClick={handleToggleSourcesExpansion}
                >
                    {showMoreButtonText}
                </Button>
            )}
        </div>
    );
};
